import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import voiceService from '../services/voiceService';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Shield,
  Search,
  Trash2,
  AlertTriangle,
  Loader2,
  UserX,
  CheckCircle2,
  Phone,
  Link2,
  User,
  FileDown,
  History,
} from "lucide-react";
import { toast } from "sonner";
import api from '../services/apiService';
import pdfService, { DeletionCertificateData } from '../services/pdfService';

interface CustomerSearchResult {
  id: string;
  phone: string;
  name: string;
  email?: string;
  created_at: string;
  appointment_count: number;
}

interface DeletionLogItem {
  id: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  customer_created_at?: string;
  deleted_appointments: number;
  deleted_conversations: number;
  deleted_at: string;
  business_name?: string;
  business_address?: string;
}

type SearchMode = 'phone' | 'name';

/**
 * `?q=<text>&mode=phone|name` (the assistant links here): what to search for,
 * and how. Without a valid `mode` a number-like text means phone, anything
 * else a name.
 */
function searchFromUrl(search: string): { term: string; mode: SearchMode } | null {
  const params = new URLSearchParams(search);
  const term = (params.get('q') ?? '').trim();
  if (!term) return null;
  const given = params.get('mode');
  const mode: SearchMode =
    given === 'phone' || given === 'name' ? given : /^[+\d\s()/.-]+$/.test(term) ? 'phone' : 'name';
  return { term, mode };
}

const formatDateTime = (iso?: string): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('de-DE', { dateStyle: 'long', timeStyle: 'medium' });
};

const logToCertificateData = (log: DeletionLogItem): DeletionCertificateData => ({
  receiptId: log.id,
  businessName: log.business_name,
  businessAddress: log.business_address,
  customerName: log.customer_name,
  customerPhone: log.customer_phone,
  customerEmail: log.customer_email,
  customerCreatedAt: log.customer_created_at,
  deletedAt: log.deleted_at,
  deletedAppointments: log.deleted_appointments,
  deletedConversations: log.deleted_conversations,
});

const PrivacyPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { search: urlSearch } = useLocation();

  const [searchMode, setSearchMode] = useState<SearchMode>('phone');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<CustomerSearchResult[]>([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchResult | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const skipNextSuggestFetch = useRef(false);
  const closeSuggestionsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestGeneration = useRef(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [receipt, setReceipt] = useState<DeletionCertificateData | null>(null);

  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
  const [privacyLoading, setPrivacyLoading] = useState(true);
  const [privacySaving, setPrivacySaving] = useState(false);

  const [history, setHistory] = useState<DeletionLogItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const settings = await voiceService.getSettings();
        if (settings.success) {
          setPrivacyPolicyUrl(settings.privacy_policy_url || '');
        }
      } catch (err) {
        console.error('Error loading privacy URL:', err);
      } finally {
        setPrivacyLoading(false);
      }
    })();
  }, [isAuthenticated]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get<{ success: boolean; logs: DeletionLogItem[] }>('/privacy/deletion-logs');
      if (response.success) {
        setHistory(response.logs);
      }
    } catch (err) {
      console.error('Error loading deletion history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadHistory();
  }, [isAuthenticated]);

  const handleSavePrivacyUrl = async () => {
    setPrivacySaving(true);
    try {
      const result = await voiceService.updateSettings({
        privacy_policy_url: privacyPolicyUrl || undefined,
      });
      if (result.success) {
        toast.success('Datenschutzerklärung-Link gespeichert');
      } else {
        toast.error(result.error || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      console.error('Error saving privacy URL:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setPrivacySaving(false);
    }
  };

  const resetResults = () => {
    setSuggestions([]);
    setSuggestionsOpen(false);
    setSelectedCustomer(null);
    setReceipt(null);
  };

  const fetchMatches = async (term: string, mode: SearchMode) => {
    return api.get<{
      success: boolean;
      customers?: CustomerSearchResult[];
      error?: string;
    }>(`/privacy/customer/search?query=${encodeURIComponent(term)}&mode=${mode}`);
  };

  // Live typeahead: narrow suggestions as the user types, debounced.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (skipNextSuggestFetch.current) {
      skipNextSuggestFetch.current = false;
      return;
    }

    const term = searchQuery.trim();
    if (!term) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      return;
    }

    const myGeneration = ++suggestGeneration.current;
    const handle = setTimeout(async () => {
      try {
        const response = await fetchMatches(term, searchMode);
        if (suggestGeneration.current !== myGeneration) return; // superseded by a newer keystroke/selection
        setSuggestions(response.customers || []);
        setSuggestionsOpen(true);
      } catch (err) {
        // Typeahead stays silent on error — the explicit search button surfaces it.
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchMode, isAuthenticated]);

  const handleSelectSuggestion = (customer: CustomerSearchResult, mode: SearchMode = searchMode) => {
    if (closeSuggestionsTimeout.current) clearTimeout(closeSuggestionsTimeout.current);
    suggestGeneration.current += 1; // invalidate any in-flight typeahead fetch
    skipNextSuggestFetch.current = true;
    setSelectedCustomer(customer);
    setSearchQuery(mode === 'phone' ? customer.phone : customer.name);
    setSuggestions([]);
    setSuggestionsOpen(false);
    setSearchError(null);
  };

  const handleInputBlur = () => {
    // Delay so a click on a dropdown row (onMouseDown below) still registers.
    closeSuggestionsTimeout.current = setTimeout(() => setSuggestionsOpen(false), 150);
  };

  const runSearch = async (rawTerm: string, mode: SearchMode) => {
    const term = rawTerm.trim();
    if (!term) {
      setSearchError(mode === 'phone' ? 'Bitte geben Sie eine Telefonnummer ein' : 'Bitte geben Sie einen Namen ein');
      return;
    }

    suggestGeneration.current += 1; // invalidate any in-flight typeahead fetch
    setSearching(true);
    setSearchError(null);
    setSelectedCustomer(null);
    setReceipt(null);

    try {
      const response = await fetchMatches(term, mode);
      const matches = response.customers || [];

      if (response.success && matches.length > 0) {
        if (matches.length === 1) {
          handleSelectSuggestion(matches[0], mode);
        } else {
          setSuggestions(matches);
          setSuggestionsOpen(true);
        }
      } else {
        setSuggestions([]);
        setSuggestionsOpen(false);
        setSearchError(response.error || 'Kein Kunde gefunden');
      }
    } catch (err: any) {
      console.error('Error searching customer:', err);
      setSearchError(err?.message || 'Fehler bei der Suche. Bitte versuchen Sie es erneut.');
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = () => runSearch(searchQuery, searchMode);

  // Opened with a search in the URL: fill in the field and run it, once.
  // Only ever a search — nothing is deleted without the owner's own click.
  const urlSearchDone = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || urlSearchDone.current) return;
    urlSearchDone.current = true;
    const request = searchFromUrl(urlSearch);
    if (!request) return;
    skipNextSuggestFetch.current = true; // the search below fills the suggestions itself
    setSearchMode(request.mode);
    setSearchQuery(request.term);
    void runSearch(request.term, request.mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleDeleteRequest = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCustomer) return;

    setDeleting(true);
    try {
      const response = await api.delete<{
        success: boolean;
        deleted_appointments?: number;
        deleted_conversations?: number;
        error?: string;
        deletion_log_id?: string;
        customer_name?: string;
        customer_phone?: string;
        customer_email?: string;
        customer_created_at?: string;
        deleted_at?: string;
        business_name?: string;
        business_address?: string;
      }>(`/privacy/customer/${selectedCustomer.id}`);

      if (response.success) {
        setDeleteDialogOpen(false);
        resetResults();
        setSearchQuery('');
        setReceipt({
          receiptId: response.deletion_log_id || '—',
          businessName: response.business_name,
          businessAddress: response.business_address,
          customerName: response.customer_name || selectedCustomer.name,
          customerPhone: response.customer_phone || selectedCustomer.phone,
          customerEmail: response.customer_email,
          customerCreatedAt: response.customer_created_at,
          deletedAt: response.deleted_at || new Date().toISOString(),
          deletedAppointments: response.deleted_appointments || 0,
          deletedConversations: response.deleted_conversations || 0,
        });
        toast.success('Kundendaten erfolgreich gelöscht');
        loadHistory();
      } else {
        toast.error(response.error || 'Fehler beim Löschen');
      }
    } catch (err: any) {
      console.error('Error deleting customer:', err);
      toast.error('Fehler beim Löschen der Kundendaten');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadCertificate = async (data: DeletionCertificateData) => {
    try {
      const pdfBytes = await pdfService.generateDeletionCertificate(data);
      const safeName = (data.customerName || 'Kunde').replace(/\s+/g, '_');
      await pdfService.downloadPDF(pdfBytes, `Loeschungsbeleg_${safeName}.pdf`);
    } catch (err) {
      console.error('Error generating deletion certificate:', err);
      toast.error('Beleg konnte nicht erstellt werden');
    }
  };

  return (
    <Layout>
      <div className="container max-w-2xl mx-auto py-8 space-y-8 animate-in fade-in duration-500">

        {/* Header */}
        <div className="border-b pb-6">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Datenschutz</h1>
          <p className="text-muted-foreground mt-1">Kundendaten verwalten und löschen</p>
        </div>

        {/* Info Alert */}
        <Alert className="border-primary/25 bg-primary/10">
          <Shield className="h-4 w-4 text-primary-strong" />
          <AlertTitle className="text-primary-strong">DSGVO-konform</AlertTitle>
          <AlertDescription className="text-primary-strong">
            Wenn ein Kunde die Löschung seiner Daten wünscht, können Sie hier alle personenbezogenen Daten
            (Name, Telefonnummer, E-Mail, Terminhistorie) unwiderruflich löschen und erhalten anschließend
            einen Löschbeleg als Nachweis.
          </AlertDescription>
        </Alert>

        {/* Deletion Certificate */}
        {receipt && (
          <Card className="shadow-sm border-border border-l-4 border-l-success">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-success-strong" />
                Erfolgreich gelöscht — Löschbeleg bereit
              </CardTitle>
              <CardDescription>
                Diese Bestätigung dient als Nachweis für Ihr Unternehmen und kann jederzeit erneut heruntergeladen werden.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground">Kunde</p>
                  <p className="font-medium">{receipt.customerName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Telefon</p>
                  <p className="font-medium">{receipt.customerPhone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Datensatz erstellt am</p>
                  <p className="font-medium">{formatDateTime(receipt.customerCreatedAt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Datensatz gelöscht am</p>
                  <p className="font-medium">{formatDateTime(receipt.deletedAt)}</p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="bg-muted/50 border-t p-4 flex justify-end">
              <Button onClick={() => handleDownloadCertificate(receipt)} className="min-w-[220px]">
                <FileDown className="h-4 w-4 mr-2" />
                Löschbeleg herunterladen (PDF)
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Privacy Policy URL Card */}
        <Card className="shadow-sm border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="h-5 w-5 text-[#1967D2]" />
              Link zur Datenschutzerklärung
            </CardTitle>
            <CardDescription>
              Wird dem Anrufer bzw. WhatsApp-Kunden vor Gesprächsbeginn rechtlich korrekt angeboten.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Label htmlFor="privacy-url">URL</Label>
            <Input
              id="privacy-url"
              type="url"
              placeholder="https://ihre-website.de/datenschutz"
              value={privacyPolicyUrl}
              onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
              disabled={privacyLoading}
              className="bg-background"
            />
          </CardContent>
          <CardFooter className="bg-muted/50 border-t p-4 flex justify-end">
            <Button
              onClick={handleSavePrivacyUrl}
              disabled={privacyLoading || privacySaving}
              className="bg-[#1967D2] hover:bg-[#1558b0] text-white min-w-[140px]"
            >
              {privacySaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Speichern
            </Button>
          </CardFooter>
        </Card>

        {/* Search Card */}
        <Card className="shadow-sm border-border">
          <CardHeader>
            <CardTitle className="text-lg">Kunden suchen</CardTitle>
            <CardDescription>Suchen Sie einen Kunden anhand seiner Telefonnummer oder seines Namens</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="inline-flex rounded-lg border border-border bg-muted/50 p-1">
              <button
                type="button"
                onClick={() => {
                  setSearchMode('phone');
                  setSearchQuery('');
                  setSearchError(null);
                  resetResults();
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                  searchMode === 'phone' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'
                }`}
              >
                <Phone className="h-3.5 w-3.5" />
                Telefonnummer
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearchMode('name');
                  setSearchQuery('');
                  setSearchError(null);
                  resetResults();
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                  searchMode === 'name' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'
                }`}
              >
                <User className="h-3.5 w-3.5" />
                Name
              </button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer-search" className="flex items-center gap-2">
                {searchMode === 'phone' ? (
                  <Phone className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <User className="h-4 w-4 text-muted-foreground" />
                )}
                {searchMode === 'phone' ? 'Telefonnummer des Kunden' : 'Name des Kunden'}
              </Label>
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="customer-search"
                    type={searchMode === 'phone' ? 'tel' : 'text'}
                    placeholder={searchMode === 'phone' ? 'z.B. 0151 12345678 oder +49151...' : 'z.B. Max Mustermann'}
                    value={searchQuery}
                    autoComplete="off"
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSearchError(null);
                    }}
                    onFocus={() => {
                      if (suggestions.length > 0) setSuggestionsOpen(true);
                    }}
                    onBlur={handleInputBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !searching) {
                        handleSearch();
                      }
                    }}
                    className="w-full"
                  />
                  {suggestionsOpen && suggestions.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-popover shadow-lg overflow-hidden">
                      {suggestions.map((customer) => (
                        <button
                          key={customer.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectSuggestion(customer)}
                          className="w-full text-left flex items-center justify-between gap-4 px-3 py-2 hover:bg-muted/70 transition-colors border-b border-border last:border-b-0"
                        >
                          <div className="min-w-0">
                            <p className="font-medium truncate">{customer.name || 'Unbekannt'}</p>
                            <p className="text-xs text-muted-foreground truncate">{customer.phone || '—'}</p>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                            {customer.appointment_count} Termine
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button onClick={handleSearch} disabled={searching} aria-label="Kunde suchen">
                  {searching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {searchError && (
                <p className="text-sm text-destructive-strong">{searchError}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Search Result */}
        {selectedCustomer && (
          <Card className="shadow-sm border-border border-l-4 border-l-amber-500">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserX className="h-5 w-5 text-warning-strong" />
                Kunde gefunden
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium">{selectedCustomer.name || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Telefon</p>
                  <p className="font-medium">{selectedCustomer.phone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">E-Mail</p>
                  <p className="font-medium">{selectedCustomer.email || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Termine</p>
                  <p className="font-medium">{selectedCustomer.appointment_count} Termine</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Datensatz erstellt am</p>
                  <p className="font-medium">{formatDateTime(selectedCustomer.created_at)}</p>
                </div>
              </div>

              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Achtung</AlertTitle>
                <AlertDescription>
                  Das Löschen ist unwiderruflich! Alle Daten dieses Kunden (Profil, Termine, Nachrichten)
                  werden dauerhaft entfernt. Sie erhalten anschließend einen Löschbeleg als Nachweis.
                </AlertDescription>
              </Alert>
            </CardContent>
            <CardFooter className="bg-muted/50 border-t p-4 flex justify-end">
              <Button
                variant="destructive"
                onClick={handleDeleteRequest}
                className="min-w-[180px]"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Alle Daten löschen
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Deletion History */}
        <Card className="shadow-sm border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-5 w-5 text-muted-foreground" />
              Löschungs-Historie
            </CardTitle>
            <CardDescription>
              Bisherige Löschbelege dieses Unternehmens — jederzeit erneut als PDF abrufbar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {historyLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                Wird geladen...
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">Noch keine Löschungen durchgeführt.</p>
            ) : (
              history.map((log) => (
                <div
                  key={log.id}
                  className="flex items-center justify-between gap-4 rounded-md border border-border p-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{log.customer_name}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {log.customer_phone} · gelöscht am {formatDateTime(log.deleted_at)}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadCertificate(logToCertificateData(log))}
                    className="shrink-0"
                  >
                    <FileDown className="h-4 w-4 mr-1.5" />
                    Beleg
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-primary-strong">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary-strong">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                Daten unwiderruflich löschen?
              </DialogTitle>
              <DialogDescription className="pt-2">
                Sie sind dabei, alle Daten von <strong>{selectedCustomer?.name || selectedCustomer?.phone}</strong> zu löschen:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Kundenprofil (Name, E-Mail, Telefon)</li>
                  <li>Alle {selectedCustomer?.appointment_count} Termine</li>
                  <li>Gesprächsverläufe und Nachrichten</li>
                </ul>
                <p className="mt-3 font-medium text-primary-strong">
                  Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deleting}
              >
                Abbrechen
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Endgültig löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PrivacyPage;
